export const allowedShareProvider = [
  "whatsapp",
  "messenger",
  "telegram",
  "viber",
  "email",
  "sms",
  "copy",
  "kakao",
] as const;

export type ShareProvider = (typeof allowedShareProvider)[number];

export const isAllowedShareProvider = (value: string): value is ShareProvider => {
  return allowedShareProvider.some((p) => p === value);
};

export type MessengerConfig = {
  appId?: string;
  redirectUri?: string;
};

export type ShareConfig = {
  buttonLabel?: string;
  shareUrl?: string;
  shareTextPrefix?: string;
  providers: ShareProvider[];
  messenger?: MessengerConfig;
  shareDialogTitle?: string;
  shareDialogDescription?: string;
};

export type BuilderShareConfig = Omit<ShareConfig, "messenger" | "providers"> & {
  providers?: string[];
  messengerConfig?: MessengerConfig;
};

type LegacyShareItem = {
  trackingName?: string;
};

export const buildShareText = (prefix: string | undefined, url: string): string => {
  const normalizedPrefix = (prefix || "").trim();
  const normalizedUrl = (url || "").trim();

  // Avoid duplication if prefix equals url
  if (normalizedPrefix === normalizedUrl) {
    return normalizedUrl;
  }

  // Handle empty cases
  if (!normalizedPrefix) {
    return normalizedUrl;
  }

  if (!normalizedUrl) {
    return normalizedPrefix;
  }

  return `${normalizedPrefix} ${normalizedUrl}`;
};

export type NativeShareResult = "shared" | "cancelled" | "unsupported";

export const tryNativeShare = async (params: ShareData = {}): Promise<NativeShareResult> => {
  if (typeof navigator === "undefined" || typeof navigator.share !== "function") {
    return "unsupported";
  }
  try {
    await navigator.share(params);
    return "shared";
  } catch {
    // User cancelled the share dialog (AbortError) or other error
    // In both cases, we don't want to show the fallback dialog
    // because the native dialog was shown
    return "cancelled";
  }
};

const enc = (v: string) => encodeURIComponent(v);

export const providerHref = (
  provider: ShareProvider,
  url: string,
  prefix?: string,
  messenger?: MessengerConfig
): string | null => {
  const text = buildShareText(prefix, url);
  switch (provider) {
    case "whatsapp":
      return `https://wa.me/?text=${enc(text)}`;
    case "telegram":
      return `https://t.me/share/url?url=${enc(url)}&text=${enc(prefix || "")}`;
    case "email": {
      const subject = prefix || "Invite";
      return `mailto:?subject=${enc(subject)}&body=${enc(text)}`;
    }
    case "sms": {
      return `sms:?&body=${enc(text)}`;
    }
    case "messenger": {
      if (messenger?.appId && messenger?.redirectUri) {
        return `https://www.facebook.com/dialog/send?link=${enc(url)}&app_id=${enc(messenger.appId)}&redirect_uri=${enc(
          messenger.redirectUri
        )}`;
      }
      return `fb-messenger://share/?link=${enc(url)}`;
    }
    case "viber":
      return `viber://forward?text=${enc(text)}`;
    case "copy":
      return url;
    default:
      return null;
  }
};

const legacyProviders: ShareProvider[] = ["whatsapp", "messenger", "telegram", "viber", "email", "sms", "copy"];
const shareProviderLookup = new Map<string, ShareProvider>(legacyProviders.map((provider) => [provider, provider]));
const copyProvider: ShareProvider = "copy";

const extractLegacyItems = (source: unknown): LegacyShareItem[] => {
  if (Array.isArray(source)) {
    return source;
  }
  if (typeof source === "object" && source !== null && "items" in source) {
    const maybeItems = Reflect.get(source, "items");
    if (Array.isArray(maybeItems)) {
      return maybeItems;
    }
  }
  return [];
};

const normalizeProvider = (value: unknown): ShareProvider | undefined => {
  if (typeof value !== "string") return undefined;
  const normalized = value.toLowerCase();
  return shareProviderLookup.get(normalized);
};

const buildShareUrlTemplate = (loyaltyReferralsPageUrl?: string): string => {
  const basePath = loyaltyReferralsPageUrl || "/loyalty/my-referrals";
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}${basePath}?code={#referralCode#}`;
};

type BuildReferralShareConfigParams = {
  shareTitle: string;
  legacyItemsSource?: unknown;
  loyaltyReferralsPageUrl?: string;
};

export const buildReferralShareConfig = ({
  shareTitle,
  legacyItemsSource,
  loyaltyReferralsPageUrl,
}: BuildReferralShareConfigParams): ShareConfig => {
  const providers: ShareProvider[] = [];
  for (const item of extractLegacyItems(legacyItemsSource)) {
    const provider = normalizeProvider(item?.trackingName);
    if (provider && !providers.includes(provider)) {
      providers.push(provider);
    }
  }
  if (!providers.includes(copyProvider)) {
    providers.push(copyProvider);
  }

  return {
    buttonLabel: shareTitle,
    shareUrl: buildShareUrlTemplate(loyaltyReferralsPageUrl),
    shareTextPrefix: shareTitle,
    providers,
  };
};

export const buildShareProviders: (providers: string[] | null | undefined) => ShareProvider[] = (providers) => {
  const ensuredProviders = [...(providers ?? [])];
  if (!ensuredProviders.includes("copy")) {
    ensuredProviders.push("copy");
  }

  return ensuredProviders.filter(isAllowedShareProvider);
};

export const buildShareConfig = (shareConfig?: BuilderShareConfig): ShareConfig | undefined => {
  if (!shareConfig) {
    return undefined;
  }

  const providers = buildShareProviders(shareConfig.providers);

  return {
    buttonLabel: shareConfig.buttonLabel ?? "",
    shareUrl: shareConfig.shareUrl ?? "",
    shareDialogTitle: shareConfig.shareDialogTitle,
    shareDialogDescription: shareConfig.shareDialogDescription,
    shareTextPrefix: shareConfig.shareTextPrefix,
    providers,
    messenger: shareConfig.messengerConfig
      ? {
          appId: shareConfig.messengerConfig.appId ?? "",
          redirectUri: shareConfig.messengerConfig.redirectUri ?? "",
        }
      : undefined,
  };
};
